import { atom, Getter } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithDefault, loadable, unwrap, useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { applyPermissionBasedFiltering } from '../../../components/ColumnManagement';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import { FieldDefinition } from '../../../data-models/field-definition';
import { columnMetaToFieldDefinition } from '../../../data-models/field-definition/column-meta-mapping';
import { formulaFieldToFieldDefinition } from '../../../data-models/field-definition/formula-field-mapping';
import { COMPANY_VIEW_TYPE } from '../../../data-models/view-config.data-model';
import { toViewSummary, ViewModel, ViewSummaryModel } from '../../../models/view.model';
import {
  fetchColumns,
  fetchPageConfig,
  fetchView,
  fetchViews,
} from '../../../services/queries/MaggieViewQueries';
import { readThroughAtom } from '../../../services/boot-cache/bootCache';
import { getErrorMessage } from '../../../services/queryHelpers';
import { AggFuncConfigs, ColumnMeta, MoicAggFuncConfig } from '../../../types';
import { formulaFieldsListLoadable } from '../../../services/state/AdminPanel/FormulaFieldsStateJ';
import {
  formulaFieldEntityForView,
  formulaFieldPath,
  formulaFieldsForEntity,
} from '../../../util/formula-fields';
import { FeatureFlags } from '../../../util/feature-flags';
import { getLdFlag } from '../../../util/ld-client';
import { selectedColumnsGridDisplayFieldsOrgState } from '../../AdminPanel/DataDisplay/DataDisplayState';
import { PredefinedColumns } from '../components/OverviewTable/columns/PredefinedColumns/PredefinedColumns';
import { pageConfigPFJ } from './PageStateJ';

type ViewsValue = ViewSummaryModel[] | Promise<ViewSummaryModel[]>;
type ViewValue = ViewModel | Promise<ViewModel>;

export const GCChartSelectedCompanyIdAtom = atom<number>(-1);

/**
 * Default fetches the current user's portfolio views, without their settings, on first subscribe: the
 * list is for navigation and ordering. Consumers using `useAtomValue` will suspend on first read; use
 * {@link viewsPFLoadableJ} for non-suspending callers.
 */
export const viewsPFJ = atomWithDefault<ViewsValue>((): Promise<ViewSummaryModel[]> =>
  // The list differs by useNewViewsP, so each answer has its own cache entry.
  readThroughAtom(viewsPFJ, `views:${getLdFlag(FeatureFlags.useNewViewsP, false)}`, fetchViews)
);

/** Bumped by "Try again" on a view that failed to load, so its next read fetches it afresh. */
export const viewReloadPFJ = atomFamily((_viewId: number) => atom(0));

/**
 * A view with its settings, fetched from GET /view/:id the first time it opens. It also holds the
 * session's edits: a company or shared view keeps them here without saving.
 */
export const viewPFJ = atomFamily((viewId: number) =>
  atomWithDefault<ViewValue>((get) => {
    get(viewReloadPFJ(viewId));
    return fetchView(viewId);
  })
);

// Like `viewsPFJ`, an unwritten `viewPFJ` keeps its resolved Promise as the value; this serves the settled view.
const viewSettledPFJ = atomFamily((viewId: number) => unwrap(viewPFJ(viewId), (prev) => prev));

/** Writes an opened view, and its entry in the list (added when the list does not have it yet). */
export const putViewPFJ = atom(null, async (get, set, view: ViewModel) => {
  set(viewPFJ(view.id), view);
  const views = await get(viewsPFJ);
  const summary = toViewSummary(view);
  set(
    viewsPFJ,
    views.some((entry) => entry.id === view.id)
      ? views.map((entry) => (entry.id === view.id ? summary : entry))
      : [...views, summary]
  );
});

export const viewsPFLoadableJ = loadable(viewsPFJ);

/** Sync primitive atom; `-1` is the unselected sentinel. */
export const selectedViewIdPFJ = atom<number>(-1);

// `viewsPFJ` keeps its resolved Promise as the value, so a plain `get` never turns synchronous;
// this serves the settled array (and the previous one while a refetch is in flight).
const viewsSettledPFJ = unwrap(viewsPFJ, (prev) => prev);

/**
 * Derived atom: the view {@link selectedViewIdPFJ} names, with its settings from {@link viewPFJ}, or
 * `null` when the list does not have it. Returns a Promise only until the list and that view first
 * load; afterwards it stays synchronous across edits and across switching back to a view already
 * opened. Declaring it `async` would hand every consumer a fresh Promise per re-derivation and
 * re-suspend them on each view switch (FD-2976, FD-5494).
 */
export const selectedViewPFJ = atom((get): ViewModel | null | Promise<ViewModel | null> => {
  const viewId = get(selectedViewIdPFJ);
  const views = get(viewsPFJ);
  if (views instanceof Promise) {
    const settled = get(viewsSettledPFJ);
    if (settled === undefined) {
      return views.then((vs) => readListedView(get, viewId, vs));
    }
    return readListedView(get, viewId, settled);
  }
  return readListedView(get, viewId, views);
});

/**
 * The selected view's list entry: what navigation needs (its key, name and type), without waiting for
 * the view's settings. Synchronous once the list has loaded.
 */
export const selectedViewSummaryPFJ = atom(
  (get): ViewSummaryModel | null | Promise<ViewSummaryModel | null> => {
    const viewId = get(selectedViewIdPFJ);
    const views = get(viewsPFJ);
    if (views instanceof Promise) {
      const settled = get(viewsSettledPFJ);
      if (settled === undefined) {
        return views.then((vs) => vs.find((view) => view.id === viewId) ?? null);
      }
      return settled.find((view) => view.id === viewId) ?? null;
    }
    return views.find((view) => view.id === viewId) ?? null;
  }
);

function readListedView(
  get: Getter,
  viewId: number,
  views: ViewSummaryModel[]
): ViewModel | null | Promise<ViewModel> {
  if (!views.some((view) => view.id === viewId)) {
    return null;
  }
  const view = get(viewPFJ(viewId));
  if (view instanceof Promise) {
    return get(viewSettledPFJ(viewId)) ?? view;
  }
  return view;
}

export const selectedViewPFLoadableJ = loadable(selectedViewPFJ);

/**
 * The column catalogue for a view type. Lazy and writable: it fetches on first read rather than
 * when the family member is created, and a frosted sample seeds it so nothing is requested.
 */
export const columnsMetaState = atomFamily((companyViewType: COMPANY_VIEW_TYPE) => {
  return atomWithDefault(() => fetchColumns(companyViewType));
});

/**
 * FD-2976: primitive-returning indirection so dependents don't recompute on
 * every `viewsPFJ` write — `selectedViewPFJ` yields a fresh object reference
 * even for writes that only touch filteredFunds/gridState.
 */
const selectedViewCompanyViewTypeJ = atom((get) => {
  return get(unwrap(selectedViewPFJ))?.companyViewType;
});

/**
 * FieldDefinitions for the selected view's columns, keyed by `path || name`
 * (matching columnMetaToFieldDefinition's key rule and the lookup in
 * columnsMetaToColumnsDef). Scoped to the selected view's `companyViewType`, so
 * keys are unique within a viewType (FD-3271 was fixed by that scoping in
 * #6005). Empty Map until the view and its columns hydrate.
 */
export const currentViewFieldDefinitions = atom((get) => {
  const companyViewType = get(selectedViewCompanyViewTypeJ);
  const res = new Map<string, FieldDefinition>();

  if (!companyViewType) {
    return res;
  }

  const rawColumns = get(unwrap(columnsMetaState(companyViewType))) ?? ([] as ColumnMeta[]);
  rawColumns.reduce((acc, column) => {
    return acc.set(column.path || column.name, columnMetaToFieldDefinition(column));
  }, res);

  // Formula fields are FE-injected columns (not in the BE /column list); merge
  // their definitions so the colDef factory resolves their formatter like any
  // other column's. Scoped like useColumns' injection: values are only
  // materialized on the rows of the entity's own views.
  const formulaEntity = formulaFieldEntityForView(companyViewType);
  if (formulaEntity != null) {
    const formulaLoad = get(formulaFieldsListLoadable);
    const formulaFields = formulaLoad.state === 'hasData' ? (formulaLoad.data ?? []) : [];
    for (const field of formulaFieldsForEntity(formulaFields, formulaEntity)) {
      res.set(formulaFieldPath(field), formulaFieldToFieldDefinition(field));
    }
  }
  return res;
});

export const columnsByPathState = atomFamily((companyViewType: COMPANY_VIEW_TYPE) => {
  return atom(async (get) => {
    const columnMeta = await get(columnsMetaState(companyViewType));
    return columnMeta.reduce((map, column) => {
      return map.set(column.path, column);
    }, new Map<string, ColumnMeta>());
  });
});

export const columnsByPathStateLoadable = atomFamily((companyViewType: COMPANY_VIEW_TYPE) => {
  return loadable(columnsByPathState(companyViewType));
});

export const columnNameToPathState = atomFamily((companyViewType: COMPANY_VIEW_TYPE) => {
  return atom(async (get) => {
    const columnMeta = await get(columnsMetaState(companyViewType));
    return columnMeta.reduce((map, column) => {
      return map.set(column.name, column.path);
    }, new Map<string, string>());
  });
});

export const columnNameToPathStateLoadable = atomFamily((companyViewType: COMPANY_VIEW_TYPE) => {
  return loadable(columnNameToPathState(companyViewType));
});

export const columnNameToDisplayNameState = atomFamily((companyViewType: COMPANY_VIEW_TYPE) => {
  return atom(async (get) => {
    const columnMeta = await get(columnsMetaState(companyViewType));
    const predefinedColumnsMeta = PredefinedColumns.getAllMeta(companyViewType);
    const columnsToHide = get(selectedColumnsGridDisplayFieldsOrgState);
    const filteredColumnMeta = applyPermissionBasedFiltering(columnMeta ?? [], columnsToHide ?? []);
    return [...filteredColumnMeta, ...predefinedColumnsMeta].reduce((map, column) => {
      return map.set(column.name, column.displayName);
    }, new Map<string, string>());
  });
});

export const aggFuncConfigState = atomFamily((companyViewType: COMPANY_VIEW_TYPE) => {
  return atom(async (get) => {
    const columnMeta = await get(columnsMetaState(companyViewType));
    const moic: Record<string, MoicAggFuncConfig> = {};

    for (const cm of columnMeta) {
      if (cm.aggFunc === 'moicGeneric' && cm.meta?.aggFuncParams?.moic) {
        moic[cm.path] = cm.meta.aggFuncParams.moic;
      }
    }

    return { moic } satisfies AggFuncConfigs;
  });
});

/**
 * Reads the selected view, with its settings, when an action runs instead of while rendering, so a
 * menu that acts on the view neither waits for its settings to show nor fails with them. `null` when
 * the view failed to load: Portfolio already says so, and an action on it does nothing.
 */
export function useReadSelectedView() {
  return useAtomCallback(
    useCallback(async (get) => {
      try {
        return await get(selectedViewPFJ);
      } catch {
        return null;
      }
    }, [])
  );
}

/**
 * Refetches the portfolio views, the selected view and the page config, and writes them to
 * {@link viewsPFJ}, {@link viewPFJ} and {@link pageConfigPFJ}. Every other opened view is fetched
 * afresh when it next opens, so no view keeps edits from before. Errors are surfaced via toast + Sentry; the
 * function never throws.
 */
export function useInvalidateViewState() {
  const { pushErrorToast } = useToastMessageState();
  return useAtomCallback(
    useCallback(
      async (get, set) => {
        try {
          const views = await fetchViews();
          const selectedViewId = get(selectedViewIdPFJ);
          const isSelectedListed = views.some((view) => view.id === selectedViewId);
          set(viewsPFJ, views);
          // Dropped rather than reset: a reset leaves an unedited view's earlier read in place, and the
          // settled fallback would serve discarded edits until the refetch lands.
          [...viewPFJ.getParams()]
            .filter((viewId) => !isSelectedListed || viewId !== selectedViewId)
            .forEach((viewId) => {
              viewPFJ.remove(viewId);
              viewSettledPFJ.remove(viewId);
            });
          // page config takes a bit to update, so fetch after views instead of in Promise.all
          const pageConfig = await fetchPageConfig();
          set(pageConfigPFJ, pageConfig);
          // Last, so a failed read keeps the list and order above; the selected view stays as it was.
          if (isSelectedListed) {
            set(viewPFJ(selectedViewId), await fetchView(selectedViewId));
          }
        } catch (err) {
          console.error(err);
          const message = getErrorMessage(err, 'Failed to refetch views');
          Sentry.logger.error(message);
          pushErrorToast({ message });
        }
      },
      [pushErrorToast]
    )
  );
}
